'use client';

import dynamic from 'next/dynamic';
import { Context, ReactNode, useState } from 'react';

import { Footer } from '@/components/Footer/Footer';
import { SecondaryHeader } from '@/components/Headers/SecondaryHeader/SecondaryHeader';
import { ProfileMenuMobile } from '@/components/ProfileMenuMobile/ProfileMenuMobile';
import { TapBar } from '@/components/TapBar/TapBar';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { RequiredHeaderContextProps } from '@/types';

import * as S from './PageWrapper.styles';

const Header = dynamic(
  () => import('@/components/Header/Header').then(mod => mod.Header),
  { ssr: false }
);

const SelectAdTypeModal = dynamic(
  () =>
    import('@/components/SelectAdTypeModal/SelectAdTypeModal').then(
      mod => mod.SelectAdTypeModal
    ),
  { ssr: false }
);

interface PageWrapperProps<C extends RequiredHeaderContextProps | undefined> {
  children: ReactNode;
  showHeader?: boolean;
  showParticipantTabs?: boolean;
  withBlur?: boolean;
  showSearch?: boolean;
  showModalSearch?: boolean;
  showSecondaryHeader?: boolean;
  context?: Context<C>;
  isSimplified?: boolean;
  hideHeaderOnTablet?: boolean;
  showTapBar?: boolean;
}

export const PageWrapper = <C extends RequiredHeaderContextProps | undefined>({
  children,
  showHeader = false,
  showParticipantTabs,
  showSearch = false,
  showModalSearch = false,
  showSecondaryHeader = false,
  withBlur = true,
  showTapBar = true,
  hideHeaderOnTablet = false,
  isSimplified = false,
  context
}: PageWrapperProps<C>) => {
  const isDownDesktop = useMediaQuery('desktop');
  const isTablet = useMediaQuery('tablet');

  const [isOpenedEntrySelect, setIsOpenedEntrySelect] =
    useState<boolean>(false);

  let isHeaderVisible = !isDownDesktop || showHeader;

  if (hideHeaderOnTablet && isTablet) {
    isHeaderVisible = false;
  }

  const isShowParticipantTabs =
    showParticipantTabs !== undefined ? showParticipantTabs : !isTablet;

  const isSearchVisible = isTablet ? showModalSearch && showSearch : showSearch;

  const isSecondaryHeaderVisible: boolean =
    !!showSecondaryHeader && isDownDesktop;

  const handleOpenEntry = () => setIsOpenedEntrySelect(true);

  return (
    <S.Wrapper $isWrapperPadding={hideHeaderOnTablet}>
      <SelectAdTypeModal
        isOpen={isOpenedEntrySelect}
        onClose={() => setIsOpenedEntrySelect(false)}
      />
      {isHeaderVisible && (
        <Header
          isSimplified={isSimplified}
          withProgressBar
          onEntryModal={handleOpenEntry}
          showParticipantTabs={isShowParticipantTabs}
          showSearch={isSearchVisible}
          withBlur={withBlur}
          context={context as unknown as Context<RequiredHeaderContextProps>}
        />
      )}
      {isSecondaryHeaderVisible && (
        <S.SecondaryHeaderWrapper>
          <SecondaryHeader withBackButton />
        </S.SecondaryHeaderWrapper>
      )}
      {isDownDesktop && <ProfileMenuMobile />}
      <S.MainContent>{children}</S.MainContent>
      <Footer />
      {!!showTapBar && <TapBar onEntryModal={handleOpenEntry} />}
    </S.Wrapper>
  );
};
